import _ from 'underscore';
import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { LocationTarget } from '../util/index';
import { BingMapsContext } from '../bing-maps-context/index';
import { bingMapsViewReducer, ActionTypes, initialState } from './bing-maps-view-reducer';
import { getStyles } from './get-styles';
import bingMapDefaultTooltip from './bing-map-default-tooltip.pug';

export const BingMapsView = React.forwardRef((props, ref) => {
  const { MapsApiHelper, loadBingMapsSdk } = useContext(BingMapsContext);
  const [{ bingMap }, dispatch] = useReducer(bingMapsViewReducer, initialState);
  const classes = useDefaultStyles(getStyles, props);

  useImperativeHandle(ref, () => ({
    zoom: (locationTarget) => {
      if (bingMap) {
        bingMap.zoomToFit(locationTarget);
      }
    },
    hideTooltip: (event) => {
      if (bingMap) {
        bingMap.hideTooltip(event);
      }
    },
    showTooltip: (event, locationTarget) => {
      if (bingMap) {
        bingMap.showTooltip(event, locationTarget);
      }
    },
  }));

  // make sure SDK is loaded
  useEffect(() => { loadBingMapsSdk(); }, [loadBingMapsSdk]);

  const {
    id,
    className,
    locationTargets,
    negativeLocationTargets,
    newLocationTargets,
    onGetTooltipHtmlContent,
  } = props;

  const divProps = _.pick(props, 'id', 'style');

  const i18n = useContext(I18nContext);
  const defaultHandleGetTooltipHtmlContent = useCallback(
    (_event, locationTarget) => bingMapDefaultTooltip({ i18n, locationTarget }),
    [i18n]
  );

  const handleGetTooltipHtmlContent = useMemo(() => {
    if (onGetTooltipHtmlContent !== undefined) {
      // Tooltip template can be overridden. To disable tooltip, it can be set to null or false.
      return onGetTooltipHtmlContent;
    }
    return defaultHandleGetTooltipHtmlContent;
  }, [onGetTooltipHtmlContent, defaultHandleGetTooltipHtmlContent]);

  useEffect(() => {
    // if Maps API has been loaded but map object not created yet
    if (MapsApiHelper != null && bingMap == null) {
      dispatch({
        type: ActionTypes.mapsApiLoaded,
        MapsApiHelper,
        element: `#${id}`,
        locationTargets,
        negativeLocationTargets,
        newLocationTargets,
        onGetTooltipHtmlContent: handleGetTooltipHtmlContent,
      });
    }
  }, [
    bingMap,
    id,
    locationTargets,
    MapsApiHelper,
    negativeLocationTargets,
    newLocationTargets,
    handleGetTooltipHtmlContent,
  ]);

  useEffect(() => {
    dispatch({
      type: ActionTypes.locationsChanged,
      locationTargets,
      negativeLocationTargets,
      newLocationTargets,
    });
  }, [locationTargets, negativeLocationTargets, newLocationTargets]);

  const ariaLabel = useMemo(() => {
    if (_.isEmpty(locationTargets) || locationTargets.length === 0) {
      return _TL_('map');
    } else if (locationTargets.length === 1) {
      return _TL_('targeted location highlighted in map');
    }
    return _TL_('targeted locations highlighted in map');
  }, [locationTargets]);

  return (
    <div
      className={classNames(className, classes.bingMapsView)}
      {...divProps}
      aria-label={i18n.getString(ariaLabel)}
    />
  );
});

BingMapsView.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  locationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)).isRequired,
  negativeLocationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)),
  newLocationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)),
  onGetTooltipHtmlContent: PropTypes.func,
};

BingMapsView.defaultProps = {
  className: undefined,
  negativeLocationTargets: [],
  newLocationTargets: [],
  onGetTooltipHtmlContent: undefined,
};
